/**
 * Sorting, filtering and column shape for a screener table.
 *
 * Pure and framework-free, so the behaviour that matters — what a sort does
 * to ties, what a range filter does to nulls, which columns a criterion even
 * has — is testable without rendering anything.
 *
 * Two rules the rest of the app depends on:
 *
 *   * `rank` is the API's answer and is never recomputed. Sorting reorders
 *     rows; it does not renumber them, and every sort falls back to rank so
 *     the same input always produces the same output.
 *   * every row stays in memory. Filtering narrows what is drawn, never what
 *     was fetched, so clearing a filter cannot need another request.
 */
import type { ScreenerRow } from "../api/screener.ts";

export type ColumnKind = "numeric" | "text" | "category";

export interface ColumnSpec {
	id: string;
	/** Header text. */
	label: string;
	/** Longer name for the filter controls, where there is room for it. */
	filterLabel: string;
	kind: ColumnKind;
	/** Right-aligned in the table, as numbers should be. */
	numeric: boolean;
}

/** Every criterion reports these; they come from named contract fields. */
export const BASE_COLUMNS: readonly ColumnSpec[] = [
	{ id: "rank", label: "#", filterLabel: "Rank", kind: "numeric", numeric: true },
	{ id: "code", label: "Code", filterLabel: "Code", kind: "text", numeric: false },
	{ id: "close", label: "Close", filterLabel: "Close", kind: "numeric", numeric: true },
	{ id: "vwap", label: "VWAP", filterLabel: "VWAP", kind: "numeric", numeric: true },
	{ id: "money_flow", label: "Money flow", filterLabel: "Money flow", kind: "numeric", numeric: true },
	{ id: "proportion", label: "Prop", filterLabel: "Proportion", kind: "numeric", numeric: true },
	{ id: "price_correlation", label: "Corr", filterLabel: "Correlation", kind: "numeric", numeric: true },
];

/**
 * Only the volume-profile criteria carry these.
 *
 * Keyed off the data rather than the slug: the API decides what a criterion
 * answers with, and a table that reads its own rows cannot disagree with it.
 * Adding them to every criterion would mean nine columns of em dashes on the
 * eight that have no profile.
 */
export const VPROFILE_COLUMNS: readonly ColumnSpec[] = [
	{ id: "zone", label: "Zone", filterLabel: "Zone", kind: "category", numeric: false },
	{ id: "prominence", label: "Prominence", filterLabel: "Prominence", kind: "numeric", numeric: true },
];

const VPROFILE_KEYS = [
	"vprofile_zone_role",
	"vprofile_zone_behavior",
	"vprofile_zone_prominence",
	"vprofile_zone_low",
	"vprofile_zone_high",
] as const;

/** True when any row actually carries profile annotations. */
export function hasVprofileData(rows: readonly ScreenerRow[]): boolean {
	return rows.some((row) => VPROFILE_KEYS.some((key) => row.extras?.[key] !== undefined));
}

export function columnsFor(rows: readonly ScreenerRow[]): readonly ColumnSpec[] {
	return hasVprofileData(rows) ? [...BASE_COLUMNS, ...VPROFILE_COLUMNS] : BASE_COLUMNS;
}

function extra(row: ScreenerRow, key: string): number | string | boolean | null | undefined {
	return row.extras?.[key];
}

function asText(value: unknown): string | null {
	if (typeof value !== "string") return null;
	const trimmed = value.trim();
	return trimmed && trimmed !== "undetermined" ? trimmed : null;
}

function asNumber(value: unknown): number | null {
	return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/**
 * The Zone category: what the profile says this price is sitting in.
 *
 * Role and behaviour are two halves of one reading ("support", "rejection"),
 * so they are one cell and one filter category rather than two columns of
 * single words. "undetermined" is the API saying it could not tell, which is
 * a missing value, not a category worth filtering on.
 */
export function zoneCategory(row: ScreenerRow): string | null {
	const role = asText(extra(row, "vprofile_zone_role"));
	const behavior = asText(extra(row, "vprofile_zone_behavior"));
	const parts = [role, behavior].filter((part): part is string => part !== null);
	return parts.length ? parts.join(" · ") : null;
}

/** The zone's price band, when the API bounded it. Both ends or neither. */
export function zoneBounds(row: ScreenerRow): { low: number; high: number } | null {
	const low = asNumber(extra(row, "vprofile_zone_low"));
	const high = asNumber(extra(row, "vprofile_zone_high"));
	return low !== null && high !== null ? { low, high } : null;
}

export function zoneProminence(row: ScreenerRow): number | null {
	return asNumber(extra(row, "vprofile_zone_prominence"));
}

/** The sortable/filterable number behind a column, or null if it has none. */
export function numericValue(row: ScreenerRow, columnId: string): number | null {
	switch (columnId) {
		case "rank":
			return row.rank;
		case "close":
			return row.close;
		case "vwap":
			return row.vwap;
		case "money_flow":
			return row.money_flow;
		case "proportion":
			return row.proportion;
		case "price_correlation":
			return row.price_correlation;
		case "prominence":
			return zoneProminence(row);
		default:
			return null;
	}
}

/** The sortable/filterable string behind a column, or null if it has none. */
export function textValue(row: ScreenerRow, columnId: string): string | null {
	if (columnId === "code") return row.code;
	if (columnId === "zone") return zoneCategory(row);
	return null;
}

// ==========
// Sorting
// ==========
export type SortDirection = "asc" | "desc";

export interface SortState {
	columnId: string;
	direction: SortDirection;
}

export const DEFAULT_SORT: SortState = { columnId: "rank", direction: "asc" };

/**
 * First click sorts ascending, second flips it. Switching column starts over
 * at ascending rather than inheriting the previous column's direction, which
 * is the behaviour that reads as "I asked for this column".
 */
export function nextSort(current: SortState, columnId: string): SortState {
	if (current.columnId !== columnId) return { columnId, direction: "asc" };
	return { columnId, direction: current.direction === "asc" ? "desc" : "asc" };
}

/** The `aria-sort` value for a header, so a screen reader can hear the order. */
export function ariaSort(sort: SortState, columnId: string): "ascending" | "descending" | "none" {
	if (sort.columnId !== columnId) return "none";
	return sort.direction === "asc" ? "ascending" : "descending";
}

/**
 * Sort, with rank as the tie-break in every direction.
 *
 * Nulls sort last either way: an unknown proportion is not the smallest
 * proportion, and flipping the direction should not promote every row the
 * API had no answer for to the top.
 */
export function sortRows(rows: readonly ScreenerRow[], sort: SortState): ScreenerRow[] {
	const sign = sort.direction === "asc" ? 1 : -1;
	const spec = [...BASE_COLUMNS, ...VPROFILE_COLUMNS].find((column) => column.id === sort.columnId);
	const numeric = spec ? spec.numeric : true;

	return [...rows].sort((left, right) => {
		const compared = numeric
			? compareNullable(numericValue(left, sort.columnId), numericValue(right, sort.columnId), sign)
			: compareNullable(textValue(left, sort.columnId), textValue(right, sort.columnId), sign);
		// Rank last and unsigned: ties resolve to the screener's own order
		// whichever way the column was sorted, so the result is stable.
		return compared !== 0 ? compared : left.rank - right.rank;
	});
}

function compareNullable<T extends number | string>(left: T | null, right: T | null, sign: number): number {
	if (left === null && right === null) return 0;
	if (left === null) return 1;
	if (right === null) return -1;
	if (typeof left === "string" && typeof right === "string") return sign * left.localeCompare(right);
	return sign * (Number(left) - Number(right));
}

// ==========
// Filtering
// ==========
export interface NumericRange {
	min?: number;
	max?: number;
}

export interface FilterState {
	/** Column id to an optional min/max. An absent bound is not a constraint. */
	ranges: Record<string, NumericRange>;
	/** Column id to the checked categories. An empty list means "all". */
	categories: Record<string, readonly string[]>;
	/** Column id to a substring. Case-insensitive, trimmed. */
	text: Record<string, string>;
}

export const EMPTY_FILTERS: FilterState = { ranges: {}, categories: {}, text: {} };

/** No constraint anywhere — used to decide whether to offer "Clear filters". */
export function isUnfiltered(filters: FilterState): boolean {
	const noRange = Object.values(filters.ranges).every(
		(range) => range.min === undefined && range.max === undefined,
	);
	const noCategory = Object.values(filters.categories).every((selected) => selected.length === 0);
	const noText = Object.values(filters.text).every((needle) => needle.trim() === "");
	return noRange && noCategory && noText;
}

/**
 * The categories actually present in this result, sorted, for the checklist.
 *
 * Derived from the rows rather than from a fixed vocabulary: the checklist
 * must never offer a box that matches nothing, and must never omit a value
 * the API did return.
 */
export function categoryOptions(rows: readonly ScreenerRow[], columnId: string): string[] {
	const seen = new Set<string>();
	for (const row of rows) {
		const value = textValue(row, columnId);
		if (value !== null) seen.add(value);
	}
	return [...seen].sort((left, right) => left.localeCompare(right));
}

/**
 * Apply every active filter. They compose with AND.
 *
 * **A bounded range excludes nulls.** Asking for "money flow at least zero"
 * cannot be answered for a row whose money flow is unknown, and treating the
 * unknown as 0 would silently invent a reading — the same mistake the em dash
 * exists to prevent. A column with neither bound set constrains nothing, so
 * its nulls stay.
 */
export function applyFilters(rows: readonly ScreenerRow[], filters: FilterState): ScreenerRow[] {
	return rows.filter((row) => {
		for (const [columnId, range] of Object.entries(filters.ranges)) {
			if (range.min === undefined && range.max === undefined) continue;
			const value = numericValue(row, columnId);
			if (value === null) return false;
			if (range.min !== undefined && value < range.min) return false;
			if (range.max !== undefined && value > range.max) return false;
		}

		for (const [columnId, selected] of Object.entries(filters.categories)) {
			if (selected.length === 0) continue;
			const value = textValue(row, columnId);
			// A row with no category cannot be one of the checked ones.
			if (value === null || !selected.includes(value)) return false;
		}

		for (const [columnId, needle] of Object.entries(filters.text)) {
			const trimmed = needle.trim().toLowerCase();
			if (!trimmed) continue;
			const value = textValue(row, columnId);
			if (value === null || !value.toLowerCase().includes(trimmed)) return false;
		}

		return true;
	});
}

/** Filter, then sort what survived. The order matters only for speed. */
export function visibleRows(
	rows: readonly ScreenerRow[],
	filters: FilterState,
	sort: SortState,
): ScreenerRow[] {
	return sortRows(applyFilters(rows, filters), sort);
}
